import { useParams, Link } from "react-router-dom"
import { Calendar, Clock, ArrowLeft, Tag } from "lucide-react"
import { blogPosts, BlogPost } from "@/lib/blog-data"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent } from "@/shared/components/ui/card"
import { Badge } from "@/shared/components/ui/badge"

export  function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const post: BlogPost | undefined = blogPosts.find((p) => p.slug === slug)

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-8">
        <h1 className="text-3xl font-bold mb-4">Artículo no encontrado</h1>
        <p className="text-muted-foreground mb-6">
          El artículo que estás buscando no existe o fue eliminado.
        </p>
        <Button asChild>
          <Link to="/blog">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Blog
          </Link>
        </Button>
      </div>
    )
  }

  // Filtrar artículos relacionados
  const relatedPosts = blogPosts
    .filter((p) => p.category === post.category && p.slug !== post.slug)
    .slice(0, 3)

  return (
    <div className="min-h-screen bg-background pt-16">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-muted/50 to-background py-12 lg:py-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
        <div className="container relative mx-auto px-8 sm:px-12 lg:px-16">
          <div className="mx-auto max-w-4xl">
            <Button asChild variant="ghost" size="sm" className="mb-6">
              <Link to="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver al Blog
              </Link>
            </Button>

            <div className="mb-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <Badge variant="secondary" className="text-sm">
                {post.category}
              </Badge>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {new Date(post.date).toLocaleDateString("es-CO", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {post.readTime} de lectura
              </span>
            </div>

            <h1 className="mb-12 text-6xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              {post.title}
            </h1>

            <div className="flex items-center gap-4">
              <img
                src={post.author.avatar || "/placeholder.svg"}
                alt={post.author.name}
                width={56}
                height={56}
                className="rounded-full"
              />
              <div>
                <p className="font-semibold">{post.author.name}</p>
                <p className="text-sm text-muted-foreground">{post.author.role}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="border-b">
        <div className="container mx-auto px-8 sm:px-12 lg:px-16">
          <div className="relative mx-auto aspect-[21/9] max-w-6xl overflow-hidden rounded-lg">
            <img
              src={post.image || "/placeholder.svg"}
              alt={post.title}
              className="object-cover w-full h-full"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-8 sm:px-12 lg:px-16">
          <div className="mx-auto max-w-6xl">
            <div
              className="prose prose-2xl prose-slate max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, "<br />") }}
            />

            {/* Tags */}
            <div className="mt-12 flex flex-wrap items-center gap-2 border-t pt-8">
              <Tag className="h-4 w-4 text-muted-foreground" />
              {post.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* CTA */}
            <Card className="mt-12 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-8 text-center">
                <h3 className="mb-2 text-2xl font-bold">¿Listo para optimizar tu facturación?</h3>
                <p className="mb-6 text-muted-foreground">
                  Descubre cómo Rapyfact puede transformar la gestión de tu institución de salud
                </p>
                <Button asChild size="lg">
                  <Link to="/contact">Solicitar Demo Gratuita</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="border-t bg-muted/30 py-12 lg:py-20">
          <div className="container mx-auto px-8 sm:px-12 lg:px-16">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold">Artículos Relacionados</h2>
              <p className="mt-2 text-muted-foreground">
                Continúa aprendiendo sobre {post.category.toLowerCase()}
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-3">
              {relatedPosts.map((relatedPost) => (
                <Card key={relatedPost.slug} className="group overflow-hidden transition-all hover:shadow-lg">
                  <CardContent className="p-0">
                    <Link to={`/blog/${relatedPost.slug}`} className="relative block aspect-[16/9] overflow-hidden">
                      <img
                        src={relatedPost.image || "/placeholder.svg"}
                        alt={relatedPost.title}
                        className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                      />
                    </Link>
                    <div className="p-6">
                      <Badge variant="secondary" className="mb-3">
                        {relatedPost.category}
                      </Badge>
                      <Link to={`/blog/${relatedPost.slug}`}>
                        <h3 className="mb-6 text-6xl font-semibold leading-tight transition-colors group-hover:text-primary line-clamp-2">
                          {relatedPost.title}
                        </h3>
                      </Link>
                      <p className="text-sm text-muted-foreground line-clamp-2">{relatedPost.excerpt}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
